// UTKARSHUDYAM — Central Service Catalog (single source of truth)
// Drives: Mega Menu, Services Explorer, Featured Services, Individual Service Pages, SEO.

import {
  Building2, Briefcase, User, Users, Factory, HeartHandshake,
  Rocket, Award, ShieldCheck, Star, Landmark,
  Receipt, FileText, ScrollText, Banknote, IndianRupee, Calculator,
  Bookmark, Copyright, Fingerprint, Scan,
  Utensils, Pill, Flame, BadgeCheck, Leaf, Recycle,
  Ship, Container, Globe2, PackageCheck, Waves,
  Truck, ShoppingCart, LineChart,
  Handshake, PieChart, Wallet,
  Monitor, TrendingUp, Megaphone
} from 'lucide-react';

// ---- SEO helpers ----
const money = 'INR';

const std = (o) => ({
  timeline: '7-15 Working Days',
  price: 'Contact for Quote',
  processingType: 'End-to-End Managed Service',
  ...o,
});

// ============================================================
// 6 MEGA-MENU CATEGORIES (used by Header dropdown)
// ============================================================
export const megaMenuCategories = [
  { id: 'business-setup', label: 'Business Setup & Registration', tagline: 'Register your company the right way',
    slugs: ['private-limited-company-registration', 'llp-registration', 'opc-registration', 'proprietorship-registration', 'partnership-firm-registration', 'section-8-company-registration'] },
  { id: 'certifications', label: 'Business Certification', tagline: 'Trust badges for your business',
    slugs: ['startup-india-certificate', 'msme-udyam-registration', 'iso-certification', 'zed-certification', 'fssai-registration'] },
  { id: 'licensing', label: 'Import-Export & Licenses', tagline: 'Cross-border and industry approvals',
    slugs: ['import-export-code', 'apeda-registration', 'cdsco-license', 'bis-certification', 'pollution-noc'] },
  { id: 'compliance', label: 'Compliance & Taxation', tagline: 'Stay compliant every month',
    slugs: ['gst-registration', 'gst-return-filing', 'income-tax-return-filing', 'tds-return-filing', 'roc-annual-compliance'] },
  { id: 'funding', label: 'Loans & Subsidy', tagline: 'Fuel your growth',
    slugs: ['business-loan', 'msme-mudra-loan', 'cgtmse-loan', 'project-report-preparation'] },
  { id: 'digital', label: 'Web & Branding', tagline: 'Grow online',
    slugs: ['trademark-registration', 'website-development', 'digital-marketing'] },
];

// ============================================================
// SERVICES EXPLORER — 6 category tabs (grouped for the homepage widget)
// ============================================================
export const explorerCategories = [
  { id: 'setup', label: 'Business Setup', desc: 'From idea to a legally registered entity.',
    slugs: ['private-limited-company-registration', 'llp-registration', 'opc-registration', 'proprietorship-registration', 'partnership-firm-registration', 'section-8-company-registration'] },
  { id: 'msme', label: 'MSME & Startup', desc: 'Certificates that unlock benefits.',
    slugs: ['startup-india-certificate', 'msme-udyam-registration', 'zed-certification'] },
  { id: 'tax', label: 'Tax & Payroll', desc: 'Every return, filed on time.',
    slugs: ['gst-registration', 'gst-return-filing', 'income-tax-return-filing', 'tds-return-filing', 'roc-annual-compliance'] },
  { id: 'ip', label: 'Brand & IP', desc: 'Protect your name and identity.',
    slugs: ['trademark-registration', 'copyright-registration', 'patent-registration'] },
  { id: 'licenses', label: 'Licenses', desc: 'Approvals to operate.',
    slugs: ['fssai-registration', 'cdsco-license', 'bis-certification', 'pollution-noc', 'iso-certification'] },
  { id: 'trade', label: 'Trade & Standards', desc: 'Selling beyond your state or country.',
    slugs: ['import-export-code', 'apeda-registration', 'business-loan', 'msme-mudra-loan', 'cgtmse-loan'] },
];

// ============================================================
// FEATURED SERVICES (popular)
// ============================================================
export const featuredSlugs = [
  'startup-india-certificate',
  'private-limited-company-registration',
  'gst-registration',
  'msme-udyam-registration',
  'iso-certification',
  'trademark-registration',
  'fssai-registration',
  'import-export-code',
];

// ============================================================
// FULL SERVICES DATA (data-driven — one entry powers one route)
// slug MUST match the route path (/services/:slug)
// ============================================================
export const services = {
  // ---------- BUSINESS SETUP ----------
  'private-limited-company-registration': std({
    slug: 'private-limited-company-registration',
    title: 'Private Limited Company Registration',
    tagline: 'Start Your Private Limited Company with Expert Compliance Support',
    category: 'business-setup', icon: Building2,
    image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=1200&q=80',
    imageAlt: 'Modern corporate office — Private Limited Company registration',
    metaTitle: 'Private Limited Company Registration in India — Utkarshudyam',
    metaDescription: 'Register your Private Limited Company online with expert help. Name approval, DSC, DIN, MOA/AOA, ROC filing and Certificate of Incorporation — end-to-end support across India.',
    short: 'Register your business as a Private Limited Company for legal recognition, limited liability and easier funding.',
    overview: 'A Private Limited Company (Pvt Ltd) is India\'s most popular business structure. It is a separate legal entity with limited liability for shareholders and is preferred by investors, banks and enterprise buyers. We handle the entire process end-to-end — from name reservation to Certificate of Incorporation.',
    whoNeeds: 'Founders raising external funding, teams of 2+ co-founders, and businesses that want a formal, scalable structure.',
    benefits: [
      { title: 'Separate Legal Entity', desc: 'Your company is legally distinct from its owners.' },
      { title: 'Limited Liability', desc: 'Personal assets are protected from business debts.' },
      { title: 'Better Credibility', desc: 'Banks, vendors and clients trust registered companies more.' },
      { title: 'Investor Friendly', desc: 'The preferred structure for angel and VC funding.' },
      { title: 'Perpetual Succession', desc: 'Company continues even if directors change.' },
      { title: 'Easier Bank Loans', desc: 'Registered companies get better loan terms.' },
    ],
    eligibility: [
      'Minimum 2 Directors (max 15), at least 1 Indian resident.',
      'Minimum 2 Shareholders (can be the same as directors).',
      'Unique proposed company name (not identical to existing).',
      'A registered office address in India.',
      'No minimum paid-up capital requirement.',
    ],
    documents: [
      'PAN card of all directors & shareholders',
      'Aadhaar card of all directors & shareholders',
      'Passport-size photograph',
      'Latest bank statement / utility bill (address proof)',
      'Registered office address proof (electricity bill / rent agreement)',
      'NOC from property owner (if rented)',
      'Digital Signature Certificate (DSC) — we arrange this',
    ],
    process: [
      { step: 1, title: 'Consultation & name shortlist', desc: 'We understand your business and shortlist 2 unique names.' },
      { step: 2, title: 'DSC & DIN', desc: 'Digital signatures and Director Identification Numbers issued.' },
      { step: 3, title: 'Name approval (RUN/SPICe+)', desc: 'File name reservation with MCA.' },
      { step: 4, title: 'Drafting MOA & AOA', desc: 'We draft the constitutional documents.' },
      { step: 5, title: 'SPICe+ filing', desc: 'Incorporation form filed with ROC.' },
      { step: 6, title: 'Certificate of Incorporation', desc: 'CIN, PAN and TAN delivered to you.' },
    ],
    faq: [
      { q: 'How long does Private Limited Company registration take?', a: 'Typically 7–15 working days after all documents are provided, subject to MCA processing time.' },
      { q: 'Is there a minimum capital requirement?', a: 'No. You can start with any authorized capital; we recommend ₹1 lakh as a practical starting point.' },
      { q: 'Can NRIs or foreigners be directors?', a: 'Yes. At least one director must be an Indian resident (in India for 182+ days).' },
      { q: 'Do I need a commercial office to register?', a: 'No. You can use a residential address as the registered office initially.' },
      { q: 'What compliances are required after incorporation?', a: 'Board meetings, annual filings (AOC-4, MGT-7), income tax filing, statutory registers and (if applicable) GST returns.' },
      { q: 'Can a Private Limited Company be converted later?', a: 'Yes — into a Public Limited Company, OPC or LLP, subject to statutory conditions.' },
    ],
    price: 'Starting ₹6,999 (incl. Govt. fee)',
    timeline: '7-15 Working Days',
    relatedSlugs: ['llp-registration', 'opc-registration', 'gst-registration', 'startup-india-certificate', 'trademark-registration'],
  }),
  'llp-registration': std({
    slug: 'llp-registration', title: 'LLP Registration',
    tagline: 'Register a Limited Liability Partnership — Professional & Cost-Effective',
    category: 'business-setup', icon: Handshake,
    image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=1200&q=80',
    imageAlt: 'Business partners meeting — LLP registration',
    metaTitle: 'LLP Registration Online in India — Utkarshudyam',
    metaDescription: 'Register your Limited Liability Partnership online. Digital signatures, name reservation, LLP Agreement drafting and MCA filing — quick, transparent, and expert-guided.',
    short: 'LLP combines partnership flexibility with company-like limited liability — ideal for professionals and services businesses.',
    overview: 'A Limited Liability Partnership is a hybrid entity governed by the LLP Act, 2008. Partners have limited liability, and compliance is lighter than a Private Limited Company — making it a popular choice for professional firms and small businesses.',
    whoNeeds: 'Consulting firms, professional service partnerships, and small businesses that want limited liability without heavy compliance.',
    benefits: [
      { title: 'Limited Liability', desc: 'Personal assets stay protected.' },
      { title: 'Lower Compliance', desc: 'Fewer annual filings than a Pvt Ltd.' },
      { title: 'No Minimum Capital', desc: 'Start with any capital contribution.' },
      { title: 'Perpetual Existence', desc: 'LLP survives changes in partners.' },
      { title: 'Tax Efficient', desc: 'No dividend distribution tax.' },
      { title: 'Flexible Structure', desc: 'Rights and duties per the LLP Agreement.' },
    ],
    eligibility: ['Minimum 2 Designated Partners.', 'At least 1 partner must be an Indian resident.', 'Unique LLP name.', 'Registered office address in India.'],
    documents: ['PAN & Aadhaar of all partners', 'Passport-size photograph', 'Address proof of partners', 'Registered office proof + NOC', 'Digital Signature Certificate (DSC)'],
    process: [
      { step: 1, title: 'DSC for partners', desc: 'Digital signatures issued.' },
      { step: 2, title: 'DIN / DPIN', desc: 'Designated Partner Identification Number.' },
      { step: 3, title: 'Name reservation (RUN-LLP)', desc: 'Unique name approval.' },
      { step: 4, title: 'FiLLiP filing', desc: 'Incorporation form with ROC.' },
      { step: 5, title: 'LLP Agreement', desc: 'Drafted and filed within 30 days.' },
      { step: 6, title: 'Certificate of Incorporation', desc: 'LLPIN issued.' },
    ],
    faq: [
      { q: 'What is the minimum capital for LLP?', a: 'There is no minimum capital requirement.' },
      { q: 'Can an LLP be converted to a Pvt Ltd?', a: 'Yes, subject to MCA rules and consent of all partners.' },
      { q: 'Is audit required for LLP?', a: 'Only if turnover > ₹40 lakh or contribution > ₹25 lakh.' },
      { q: 'Are LLPs eligible for FDI?', a: 'Yes, under the automatic route in most sectors.' },
      { q: 'How many partners are allowed?', a: 'Minimum 2, no maximum limit.' },
    ],
    price: 'Starting ₹5,999', timeline: '10-15 Working Days',
    relatedSlugs: ['private-limited-company-registration', 'partnership-firm-registration', 'gst-registration'],
  }),
  'opc-registration': std({
    slug: 'opc-registration', title: 'OPC Registration',
    tagline: 'One Person Company — Solo Entrepreneur, Corporate Benefits',
    category: 'business-setup', icon: User,
    image: 'https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=1200&q=80',
    imageAlt: 'Solo entrepreneur — One Person Company registration',
    metaTitle: 'One Person Company (OPC) Registration Online — Utkarshudyam',
    metaDescription: 'Register your One Person Company. Perfect for solo founders — limited liability and separate legal identity with simplified compliance.',
    short: 'OPC lets a single founder enjoy corporate benefits with limited liability and easy compliance.',
    overview: 'One Person Company (OPC) is a company owned by a single member. It gives solo entrepreneurs the benefits of a Private Limited structure with a simpler compliance regime.',
    whoNeeds: 'Solo founders, freelancers scaling up, and single-owner businesses seeking limited liability.',
    benefits: [
      { title: 'Single Member', desc: 'Complete control with only one owner.' },
      { title: 'Limited Liability', desc: 'Owner\'s personal assets are protected.' },
      { title: 'Separate Legal Entity', desc: 'OPC is legally distinct from the owner.' },
      { title: 'Easier Compliance', desc: 'Fewer meetings and filings.' },
      { title: 'Convertible', desc: 'Can convert to Pvt Ltd on crossing thresholds.' },
    ],
    eligibility: ['Only an Indian resident individual can register.', 'One member and one nominee required.', 'A person can be a member in only one OPC at a time.'],
    documents: ['PAN & Aadhaar', 'Passport-size photograph', 'Address proof', 'Registered office proof + NOC', 'DSC'],
    process: [
      { step: 1, title: 'DSC', desc: 'For the sole member.' },
      { step: 2, title: 'Name reservation', desc: 'MCA name approval.' },
      { step: 3, title: 'SPICe+ filing', desc: 'Incorporation form.' },
      { step: 4, title: 'MOA & AOA', desc: 'Drafted with sole member details.' },
      { step: 5, title: 'Certificate of Incorporation', desc: 'CIN, PAN, TAN issued.' },
    ],
    faq: [
      { q: 'Who can register an OPC?', a: 'Any Indian resident individual (not NRI or minor).' },
      { q: 'Is a nominee mandatory?', a: 'Yes, one nominee must be named at incorporation.' },
      { q: 'When must an OPC be converted?', a: 'If paid-up capital exceeds ₹50 lakh or turnover exceeds ₹2 crore.' },
      { q: 'Can OPC be started for any business?', a: 'Yes, except NBFC / investment-related activity.' },
    ],
    price: 'Starting ₹5,999', timeline: '10-15 Working Days',
    relatedSlugs: ['private-limited-company-registration', 'proprietorship-registration', 'gst-registration'],
  }),
  'proprietorship-registration': std({
    slug: 'proprietorship-registration', title: 'Proprietorship Registration',
    tagline: 'The Simplest Way to Start a Business',
    category: 'business-setup', icon: Briefcase,
    image: 'https://images.unsplash.com/photo-1556740738-b6a63e27c4df?w=1200&q=80',
    imageAlt: 'Small business owner — Proprietorship registration',
    metaTitle: 'Proprietorship Registration Online in India — Utkarshudyam',
    metaDescription: 'Start your Sole Proprietorship quickly with GST, MSME, Shop Act and current account setup — expert-guided end-to-end.',
    short: 'Fastest and lowest-cost way to launch a business — perfect for solo entrepreneurs and traders.',
    overview: 'A Sole Proprietorship is an unregistered form of business owned and controlled by one person. Legally, it uses the owner\'s identity — but government registrations like GST, MSME and Shop Act give it operational recognition.',
    whoNeeds: 'Small traders, freelancers, service providers and shop owners.',
    benefits: [
      { title: 'Complete Control', desc: 'All decisions rest with the owner.' },
      { title: 'Minimal Compliance', desc: 'No ROC filings or board meetings.' },
      { title: 'Simple Taxation', desc: 'Filed as personal ITR.' },
      { title: 'Fast Setup', desc: 'Can be operational within days.' },
      { title: 'Low Cost', desc: 'Cheapest structure to start.' },
    ],
    eligibility: ['Any Indian resident can start.', 'PAN and Aadhaar required.'],
    documents: ['PAN & Aadhaar', 'Address proof', 'Bank details', 'Business address proof (utility bill/rent)'],
    process: [
      { step: 1, title: 'Consultation', desc: 'We understand your business.' },
      { step: 2, title: 'GST / MSME', desc: 'Registrations that give business identity.' },
      { step: 3, title: 'Shop Act', desc: 'State-level establishment license (if needed).' },
      { step: 4, title: 'Current account setup', desc: 'Bank account in business name.' },
    ],
    faq: [
      { q: 'Is proprietorship separately registered?', a: 'No, it uses your identity. GST, MSME and Shop Act give it recognition.' },
      { q: 'Can I hire employees?', a: 'Yes — subject to labour law compliance based on headcount.' },
      { q: 'Is my liability limited?', a: 'No — proprietors have unlimited personal liability.' },
    ],
    price: 'Starting ₹1,999', timeline: '3-7 Working Days',
    relatedSlugs: ['gst-registration', 'msme-udyam-registration', 'opc-registration'],
  }),
  'partnership-firm-registration': std({
    slug: 'partnership-firm-registration', title: 'Partnership Firm Registration',
    tagline: 'Register a Traditional Partnership with a Legal Deed',
    category: 'business-setup', icon: Users,
    image: 'https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=1200&q=80',
    imageAlt: 'Business partners shaking hands — Partnership firm registration',
    metaTitle: 'Partnership Firm Registration in India — Utkarshudyam',
    metaDescription: 'Register your Partnership Firm with a well-drafted Partnership Deed, PAN, GST and bank account setup — fully guided.',
    short: 'A cost-effective structure for 2+ partners with flexible profit sharing.',
    overview: 'A Partnership Firm is governed by the Indian Partnership Act, 1932. Two or more people share ownership, profits and responsibilities under a written Partnership Deed. Registration with the Registrar of Firms is optional but recommended.',
    whoNeeds: 'Family businesses, small trading firms and professional co-founders.',
    benefits: [
      { title: 'Easy Formation', desc: 'Set up quickly with a partnership deed.' },
      { title: 'Flexible Sharing', desc: 'Profits, roles and rights per the deed.' },
      { title: 'Low Cost', desc: 'Very affordable to start and run.' },
      { title: 'Minimal Compliance', desc: 'No ROC filings.' },
    ],
    eligibility: ['Minimum 2 partners (max 20).', 'Written Partnership Deed.', 'A place of business in India.'],
    documents: ['PAN & Aadhaar of all partners', 'Address proof of partners', 'Registered office proof', 'Partnership Deed (drafted by us)'],
    process: [
      { step: 1, title: 'Deed drafting', desc: 'We draft your Partnership Deed.' },
      { step: 2, title: 'Stamping & notarization', desc: 'On appropriate stamp paper.' },
      { step: 3, title: 'PAN application', desc: 'Firm-level PAN issued.' },
      { step: 4, title: 'ROF registration (optional)', desc: 'Registrar of Firms filing.' },
      { step: 5, title: 'GST & Bank account', desc: 'For operational readiness.' },
    ],
    faq: [
      { q: 'Is ROF registration mandatory?', a: 'No, but a registered firm can sue and enforce contractual rights.' },
      { q: 'Do partners have limited liability?', a: 'No — partnership firm partners have unlimited liability (unlike LLP).' },
      { q: 'How is a firm taxed?', a: 'At a flat 30% + surcharge + cess on business income.' },
    ],
    price: 'Starting ₹4,499', timeline: '5-10 Working Days',
    relatedSlugs: ['llp-registration', 'private-limited-company-registration', 'gst-registration'],
  }),
  'section-8-company-registration': std({
    slug: 'section-8-company-registration', title: 'Section 8 Company / NGO Registration',
    tagline: 'Register a Not-for-Profit Company for Social Impact',
    category: 'business-setup', icon: HeartHandshake,
    image: 'https://images.unsplash.com/photo-1593113598332-cd288d649433?w=1200&q=80',
    imageAlt: 'Charity work — Section 8 Company registration',
    metaTitle: 'Section 8 Company / NGO Registration in India — Utkarshudyam',
    metaDescription: 'Register your not-for-profit as a Section 8 Company. 80G, 12A and CSR-1 support to unlock donations and funding.',
    short: 'A Section 8 Company is a not-for-profit entity focused on charitable / social objectives.',
    overview: 'Section 8 Companies are registered for promoting commerce, art, science, education, sports, research, social welfare, religion, charity or environmental protection. Profits (if any) are re-invested into the objectives.',
    whoNeeds: 'Founders launching NGOs, charitable initiatives and social enterprises.',
    benefits: [
      { title: 'Credibility', desc: 'Higher trust than trusts/societies.' },
      { title: 'Tax Benefits', desc: '80G & 12A allow donor deductions.' },
      { title: 'CSR Eligible', desc: 'Corporate CSR funding possible.' },
      { title: 'Perpetual Succession', desc: 'Independent of members.' },
    ],
    eligibility: ['Minimum 2 directors and 2 members.', 'Objectives must be charitable / non-profit.', 'No dividend distribution to members.'],
    documents: ['PAN & Aadhaar of directors/members', 'Address proof', 'Registered office proof + NOC', 'Objects & activities note', 'DSC'],
    process: [
      { step: 1, title: 'DSC & DIN', desc: 'For directors.' },
      { step: 2, title: 'Name reservation', desc: 'Ending with Foundation/Association etc.' },
      { step: 3, title: 'License under Section 8', desc: 'INC-12 filed with ROC.' },
      { step: 4, title: 'Incorporation', desc: 'SPICe+ filing.' },
      { step: 5, title: '80G / 12A (optional)', desc: 'To unlock donor tax benefits.' },
    ],
    faq: [
      { q: 'Can members receive salary?', a: 'Reasonable remuneration for services is allowed; no profit distribution.' },
      { q: 'Is FCRA needed for foreign donations?', a: 'Yes, if you plan to receive foreign contributions.' },
      { q: 'How long does registration take?', a: '20–30 working days including Section 8 licence.' },
    ],
    price: 'Starting ₹9,999', timeline: '20-30 Working Days',
    relatedSlugs: ['trademark-registration', 'gst-registration'],
  }),

  // ---------- CERTIFICATIONS ----------
  'startup-india-certificate': std({
    slug: 'startup-india-certificate', title: 'Startup India (DPIIT) Certificate',
    tagline: 'Get DPIIT Recognition and Unlock Startup India Benefits',
    category: 'certifications', icon: Rocket,
    image: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=1200&q=80',
    imageAlt: 'Startup team collaboration — DPIIT recognition',
    metaTitle: 'Startup India (DPIIT) Certificate Registration — Utkarshudyam',
    metaDescription: 'Get DPIIT recognition for your startup. Tax exemptions, IPR fast-track, easier compliance and access to government schemes — expert filing support.',
    short: 'Government recognition that unlocks tax breaks, easier IPR filing and access to funding schemes.',
    overview: 'The Department for Promotion of Industry and Internal Trade (DPIIT) provides Startup India recognition to eligible startups. It is issued to entities under 10 years old, with turnover under ₹100 crore, and working on innovation, development or improvement of products/services.',
    whoNeeds: 'Early-stage startups (Pvt Ltd, LLP or Partnership) building innovative or scalable products/services.',
    benefits: [
      { title: 'Income Tax Exemption', desc: '3-year tax holiday under Section 80-IAC (subject to eligibility).' },
      { title: 'IPR Fast-Track', desc: '80% rebate on patent filings and fast-track examination.' },
      { title: 'Easier Compliance', desc: 'Self-certification under 9 labour and 3 environment laws.' },
      { title: 'Government Tenders', desc: 'EMD & prior-experience relaxation.' },
      { title: 'Fund of Funds', desc: 'Access to Startup India Seed Fund Scheme.' },
    ],
    eligibility: ['Entity is a Pvt Ltd, LLP or Registered Partnership.', 'Age of entity ≤ 10 years.', 'Turnover ≤ ₹100 crore in any FY.', 'Working on innovation / scalable business.', 'Not formed by splitting an existing business.'],
    documents: ['Certificate of Incorporation', 'PAN of entity', 'Brief description of business / innovation', 'Website / pitch deck (recommended)', 'Founders\' details'],
    process: [
      { step: 1, title: 'Eligibility check', desc: 'We review your business and innovation.' },
      { step: 2, title: 'Application drafting', desc: 'Business note, innovation description, uniqueness.' },
      { step: 3, title: 'DPIIT portal filing', desc: 'Complete online submission.' },
      { step: 4, title: 'Query handling', desc: 'Reply to any DPIIT clarification.' },
      { step: 5, title: 'Certificate issued', desc: 'DPIIT recognition certificate delivered.' },
    ],
    faq: [
      { q: 'Is Startup India recognition free?', a: 'The government fee is zero. Our fee covers professional drafting and filing.' },
      { q: 'What is 80-IAC exemption?', a: 'A 3-year 100% income tax deduction, available for eligible DPIIT-recognised startups on a separate application.' },
      { q: 'Can proprietorships apply?', a: 'No — only Pvt Ltd, LLP or registered Partnership firms are eligible.' },
      { q: 'How long is recognition valid?', a: 'Valid until the entity completes 10 years or crosses ₹100 crore turnover.' },
    ],
    price: 'Starting ₹4,999', timeline: '5-15 Working Days',
    relatedSlugs: ['msme-udyam-registration', 'trademark-registration', 'private-limited-company-registration'],
  }),
  'msme-udyam-registration': std({
    slug: 'msme-udyam-registration', title: 'MSME / Udyam Registration',
    tagline: 'Register as an MSME and Unlock Government Support',
    category: 'certifications', icon: Factory,
    image: 'https://images.unsplash.com/photo-1581091012184-7c39d0e05a92?w=1200&q=80',
    imageAlt: 'Small manufacturing unit — MSME Udyam registration',
    metaTitle: 'MSME / Udyam Registration Online — Utkarshudyam',
    metaDescription: 'Get your MSME Udyam certificate to access subsidies, lower interest rates, tender preference and other MSME benefits.',
    short: 'A free government registration for Micro, Small and Medium enterprises to access exclusive benefits.',
    overview: 'Udyam Registration is the official MSME registration under the Ministry of MSME. It classifies enterprises based on investment in plant & machinery and annual turnover, and unlocks a suite of government benefits.',
    whoNeeds: 'Any manufacturing or service enterprise (Proprietorship, Partnership, LLP, Pvt Ltd, HUF etc.).',
    benefits: [
      { title: 'Subsidy Access', desc: 'CLCSS, technology and marketing subsidies.' },
      { title: 'Bank Loan Priority', desc: 'Priority-sector lending with lower interest.' },
      { title: 'Tender Preference', desc: 'Exclusive quotas in government tenders.' },
      { title: 'Late Payment Protection', desc: 'Legal protection under MSMED Act.' },
      { title: 'ISO Reimbursement', desc: 'Reimbursement for certain certifications.' },
    ],
    eligibility: ['Any enterprise below the prescribed investment and turnover limits.', 'Valid PAN and GST (if applicable).'],
    documents: ['Aadhaar of proprietor / partner / director', 'PAN of entity', 'Business details (activity, employees, investment)'],
    process: [
      { step: 1, title: 'Data collection', desc: 'Investment, turnover, employee details.' },
      { step: 2, title: 'Online filing', desc: 'Filing on the Udyam portal.' },
      { step: 3, title: 'Certificate issued', desc: 'Udyam certificate with URN.' },
    ],
    faq: [
      { q: 'Is Udyam registration free?', a: 'Government fee is zero. Our fee covers professional filing and documentation.' },
      { q: 'How is MSME classified?', a: 'Based on investment in plant & machinery and turnover — Micro / Small / Medium.' },
      { q: 'Is it valid for lifetime?', a: 'Yes — no renewal required, but updates must be filed as thresholds change.' },
    ],
    price: 'Starting ₹999', timeline: '1-3 Working Days',
    relatedSlugs: ['startup-india-certificate', 'gst-registration', 'business-loan'],
  }),
  'iso-certification': std({
    slug: 'iso-certification', title: 'ISO Certification',
    tagline: 'International Quality Standards for Your Business',
    category: 'certifications', icon: Award,
    image: 'https://images.unsplash.com/photo-1616401784845-180882ba9ba8?w=1200&q=80',
    imageAlt: 'ISO quality certificate — ISO certification',
    metaTitle: 'ISO Certification (9001, 14001, 27001, 22000) — Utkarshudyam',
    metaDescription: 'Get ISO certified — 9001, 14001, 22000, 27001 and more. Documentation, audit support and certification with globally accredited bodies.',
    short: 'Globally recognized quality, environment, food-safety and information-security standards.',
    overview: 'ISO Certification demonstrates your commitment to internationally recognized management systems — improving credibility, operations and market access. We support ISO 9001 (Quality), 14001 (Environment), 22000 (Food Safety), 27001 (Information Security), 45001 (OH&S), 13485 (Medical Devices) and more.',
    whoNeeds: 'Any organisation that wants to formalise processes, win larger clients, or comply with buyer requirements.',
    benefits: [
      { title: 'Global Recognition', desc: 'Accepted worldwide by buyers and partners.' },
      { title: 'Better Quality', desc: 'Process discipline and continuous improvement.' },
      { title: 'Tender Eligibility', desc: 'Required by many government and enterprise tenders.' },
      { title: 'Customer Trust', desc: 'Signals reliability and consistency.' },
      { title: 'MSME Reimbursement', desc: 'Government reimbursement for MSMEs (subject to scheme rules).' },
    ],
    eligibility: ['Any registered business.', 'Operations in scope of the chosen ISO standard.'],
    documents: ['Business registration proof', 'Products / services details', 'Address and contact information', 'Existing quality documents (if any)'],
    process: [
      { step: 1, title: 'Gap analysis', desc: 'Identify what your current system lacks.' },
      { step: 2, title: 'Documentation', desc: 'Manuals, procedures and records.' },
      { step: 3, title: 'Implementation', desc: 'Training your team and running the system.' },
      { step: 4, title: 'Stage 1 audit', desc: 'Documentation review by certification body.' },
      { step: 5, title: 'Stage 2 audit', desc: 'On-site audit for conformity.' },
      { step: 6, title: 'Certificate issued', desc: 'Valid for 3 years with annual surveillance.' },
    ],
    faq: [
      { q: 'Which ISO standard do I need?', a: 'Depends on your industry — we do a free suitability call to advise.' },
      { q: 'Is ISO 9001 mandatory?', a: 'No, but strongly required by enterprise buyers and many tenders.' },
      { q: 'How long is an ISO certificate valid?', a: '3 years, with annual surveillance audits.' },
      { q: 'How long does the process take?', a: '15–30 days depending on standard and organisational readiness.' },
    ],
    price: 'Starting ₹6,999', timeline: '15-30 Working Days',
    relatedSlugs: ['msme-udyam-registration', 'bis-certification', 'startup-india-certificate'],
  }),
  'zed-certification': std({
    slug: 'zed-certification', title: 'ZED Certification',
    tagline: 'Zero Defect Zero Effect Certification for MSMEs',
    category: 'certifications', icon: BadgeCheck,
    image: 'https://images.unsplash.com/photo-1581092160607-7bb7bfa3e2b6?w=1200&q=80',
    imageAlt: 'MSME manufacturing quality — ZED certification',
    metaTitle: 'ZED Certification for MSMEs — Utkarshudyam',
    metaDescription: 'Get ZED (Zero Defect Zero Effect) certification. Bronze, Silver, Gold levels with government subsidy and enhanced credibility.',
    short: 'A national MSME quality scheme with three levels — Bronze, Silver, Gold — and government subsidies.',
    overview: 'ZED is a Ministry of MSME scheme that helps micro, small and medium enterprises adopt quality tools and sustainable practices. Certification comes at Bronze, Silver and Gold levels with subsidies and market advantages.',
    whoNeeds: 'Manufacturing MSMEs that want quality recognition and cost benefits.',
    benefits: [
      { title: 'Government Subsidy', desc: 'Subsidy on certification and consultancy costs.' },
      { title: 'Better Market Access', desc: 'ZED-certified units get buyer preference.' },
      { title: 'Quality Improvement', desc: 'Structured framework to reduce defects.' },
      { title: 'Reduced Wastage', desc: 'Lean and sustainability focus.' },
    ],
    eligibility: ['Registered MSME with Udyam certificate.', 'Manufacturing activity.'],
    documents: ['Udyam certificate', 'GST certificate', 'Manufacturing details', 'Address proof'],
    process: [
      { step: 1, title: 'Bronze self-assessment', desc: 'Free online pledge and profile.' },
      { step: 2, title: 'Silver / Gold documentation', desc: 'Quality documentation prepared.' },
      { step: 3, title: 'Assessment', desc: 'On-site assessment by empanelled body.' },
      { step: 4, title: 'Certification', desc: 'Level certified based on scoring.' },
    ],
    faq: [
      { q: 'Is ZED only for manufacturing?', a: 'Primarily yes, but the scheme has been expanding to include services.' },
      { q: 'How long is ZED valid?', a: 'Typically 3 years, subject to scheme guidelines.' },
    ],
    price: 'Starting ₹9,999', timeline: '30-60 Working Days',
    relatedSlugs: ['msme-udyam-registration', 'iso-certification'],
  }),
  'fssai-registration': std({
    slug: 'fssai-registration', title: 'FSSAI Food License',
    tagline: 'Get Your FSSAI Registration or License to Operate Legally',
    category: 'licensing', icon: Utensils,
    image: 'https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=1200&q=80',
    imageAlt: 'Food safety — FSSAI license',
    metaTitle: 'FSSAI Food License Online — Basic, State, Central — Utkarshudyam',
    metaDescription: 'Get FSSAI food license — Basic Registration, State License, or Central License. Complete documentation and portal filing.',
    short: 'Mandatory food safety license from FSSAI for anyone handling food commercially.',
    overview: 'The Food Safety and Standards Authority of India (FSSAI) issues three levels of registration depending on turnover and scale. Every food business operator (FBO) — from home baker to large manufacturer — needs FSSAI.',
    whoNeeds: 'Restaurants, cloud kitchens, food manufacturers, importers, wholesalers, retailers, transporters, e-commerce food sellers.',
    benefits: [
      { title: 'Legal Compliance', desc: 'Avoid penalties for operating without FSSAI.' },
      { title: 'Consumer Trust', desc: 'The 14-digit FSSAI number builds credibility.' },
      { title: 'E-commerce Enablement', desc: 'Required to sell on Zomato, Swiggy, Amazon etc.' },
      { title: 'Business Expansion', desc: 'Basis for higher licenses as you grow.' },
    ],
    eligibility: ['Any food business operator in India.', 'Basic (< ₹12 lakh turnover), State (₹12 lakh–₹20 crore), Central (> ₹20 crore or import/export).'],
    documents: ['PAN & Aadhaar of proprietor / partners / directors', 'Business address proof', 'Food category & product list', 'Passport photo', 'Layout plan (for manufacturers / State/Central)', 'NOC / rent agreement'],
    process: [
      { step: 1, title: 'Category confirmation', desc: 'We identify the correct license level.' },
      { step: 2, title: 'Documentation', desc: 'All required documents prepared.' },
      { step: 3, title: 'Portal filing', desc: 'Application submitted on FoSCoS.' },
      { step: 4, title: 'Inspection (if needed)', desc: 'For State / Central applications.' },
      { step: 5, title: 'License issued', desc: '14-digit FSSAI number delivered.' },
    ],
    faq: [
      { q: 'How long is FSSAI valid?', a: '1 to 5 years — renewable before expiry.' },
      { q: 'Which license do home bakers need?', a: 'Usually Basic Registration; upgrade if turnover crosses ₹12 lakh.' },
      { q: 'Is FSSAI mandatory for cloud kitchens?', a: 'Yes.' },
    ],
    price: 'Starting ₹1,499', timeline: '7-15 Working Days',
    relatedSlugs: ['gst-registration', 'trademark-registration', 'iso-certification'],
  }),

  // ---------- COMPLIANCE & TAX ----------
  'gst-registration': std({
    slug: 'gst-registration', title: 'GST Registration',
    tagline: 'Get Your GSTIN — Mandatory for Most Businesses',
    category: 'compliance', icon: Receipt,
    image: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=1200&q=80',
    imageAlt: 'GST tax invoice — GST registration',
    metaTitle: 'Online GST Registration in India — Utkarshudyam',
    metaDescription: 'Get your GSTIN online with expert support. Regular, Composition and Casual Taxable Person registration. End-to-end documentation and filing.',
    short: 'A unique 15-digit GSTIN required to legally collect GST and claim Input Tax Credit.',
    overview: 'GST Registration is mandatory for most businesses with turnover above ₹40 lakh (₹20 lakh for services). It is also compulsory for e-commerce sellers, interstate suppliers, and specified categories — regardless of turnover.',
    whoNeeds: 'Businesses selling goods/services above threshold, e-commerce sellers, exporters, and interstate suppliers.',
    benefits: [
      { title: 'Legal Compliance', desc: 'Collect GST and issue tax invoices.' },
      { title: 'Input Tax Credit', desc: 'Claim credit on purchases.' },
      { title: 'Pan-India Business', desc: 'Sell across states without restrictions.' },
      { title: 'Tender Eligibility', desc: 'Required for most government tenders.' },
      { title: 'Customer Trust', desc: 'GSTIN adds legitimacy to your business.' },
    ],
    eligibility: ['Turnover > ₹40 lakh (goods) or > ₹20 lakh (services).', 'Or you are an e-commerce seller, interstate supplier, casual taxable person or in a notified special category.'],
    documents: ['PAN of entity / proprietor', 'Aadhaar of proprietor / partners / directors', 'Photograph', 'Business address proof (electricity bill / rent agreement)', 'NOC (if rented)', 'Bank details / cancelled cheque', 'MOA/AOA or Partnership Deed'],
    process: [
      { step: 1, title: 'Consultation', desc: 'We confirm the correct registration type.' },
      { step: 2, title: 'Document collection', desc: 'Guided checklist and pickup.' },
      { step: 3, title: 'REG-01 filing', desc: 'Portal application with all details.' },
      { step: 4, title: 'ARN generation', desc: 'Acknowledgement receipt.' },
      { step: 5, title: 'GSTIN issued', desc: 'Certificate delivered digitally.' },
    ],
    faq: [
      { q: 'Is GST registration mandatory?', a: 'Yes, if turnover exceeds threshold or your activity is notified (e-commerce, interstate, imports, etc.).' },
      { q: 'What is Composition Scheme?', a: 'A simplified scheme with lower rates and quarterly returns for turnover up to ₹1.5 crore (goods) / ₹50 lakh (services).' },
      { q: 'How long does registration take?', a: '3–7 working days if documents are in order.' },
      { q: 'Is there a government fee?', a: 'No — the government does not charge for GST registration.' },
    ],
    price: 'Starting ₹1,499', timeline: '3-7 Working Days',
    relatedSlugs: ['gst-return-filing', 'msme-udyam-registration', 'income-tax-return-filing'],
  }),
  'gst-return-filing': std({
    slug: 'gst-return-filing', title: 'GST Return Filing',
    tagline: 'Timely GST Returns — No Notices, No Penalties',
    category: 'compliance', icon: FileText,
    image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=1200&q=80',
    imageAlt: 'GST returns filing on laptop — GST return filing',
    metaTitle: 'GST Return Filing — Monthly, Quarterly & Annual — Utkarshudyam',
    metaDescription: 'Complete GST return filing — GSTR-1, GSTR-3B, GSTR-9. Expert reconciliation, ITC matching and on-time submission.',
    short: 'Monthly / quarterly / annual GST returns filed by experts, with ITC reconciliation.',
    overview: 'Every GST-registered business must file returns even if there are no transactions. We handle GSTR-1 (outward supplies), GSTR-3B (summary & payment), GSTR-9 (annual) and reconciliation between books and portal.',
    whoNeeds: 'All GST-registered businesses.',
    benefits: [
      { title: 'Avoid Penalties', desc: 'On-time filing eliminates late fees and interest.' },
      { title: 'Maximise ITC', desc: 'Careful reconciliation with GSTR-2B.' },
      { title: 'Notice Handling', desc: 'Support for departmental notices.' },
      { title: 'Compliance Health', desc: 'Regular MIS on filing status.' },
    ],
    eligibility: ['Any GST-registered business.'],
    documents: ['Sales & purchase data', 'HSN summary', 'ITC ledger', 'E-way bill data (if applicable)'],
    process: [
      { step: 1, title: 'Data receipt', desc: 'Books, invoices and ledgers received.' },
      { step: 2, title: 'Reconciliation', desc: 'GSTR-2B and books matched.' },
      { step: 3, title: 'Return preparation', desc: 'GSTR-1 and GSTR-3B drafted.' },
      { step: 4, title: 'Client approval', desc: 'You confirm before submission.' },
      { step: 5, title: 'Filing & payment', desc: 'Portal filing and challan payment.' },
    ],
    faq: [
      { q: 'What is GSTR-3B?', a: 'A monthly summary return with output tax, ITC and net GST payable.' },
      { q: 'What if I miss a due date?', a: 'Late fee and interest apply. We help minimise the impact.' },
      { q: 'Is Nil return required?', a: 'Yes — even if there are no transactions.' },
    ],
    price: 'Starting ₹499/month', timeline: 'Monthly / Quarterly cycles',
    relatedSlugs: ['gst-registration', 'income-tax-return-filing', 'tds-return-filing'],
  }),
  'income-tax-return-filing': std({
    slug: 'income-tax-return-filing', title: 'Income Tax Return Filing',
    tagline: 'File Your ITR Correctly — Maximise Refunds, Avoid Notices',
    category: 'compliance', icon: Calculator,
    image: 'https://images.unsplash.com/photo-1638913971789-667bbaa02d54?w=1200&q=80',
    imageAlt: 'Income tax return preparation — ITR filing',
    metaTitle: 'Income Tax Return (ITR) Filing Online — Utkarshudyam',
    metaDescription: 'File your ITR with expert CAs. Salaried, business, capital gains, foreign income — all ITR forms handled with maximum refund.',
    short: 'Personal and business ITR filing with expert review and refund maximisation.',
    overview: 'Every individual, HUF, firm, LLP and company earning above the exemption limit must file an Income Tax Return. Filing correctly protects refunds, avoids notices and keeps your finances audit-ready.',
    whoNeeds: 'Salaried individuals, freelancers, business owners, LLPs, companies and NRIs.',
    benefits: [
      { title: 'Refund Maximisation', desc: 'Every deduction and exemption claimed.' },
      { title: 'Notice-Free Filing', desc: 'CA review reduces notice risk.' },
      { title: 'Loan Ready', desc: 'ITRs are needed for loans and visas.' },
      { title: 'Carry-Forward Losses', desc: 'On-time filing preserves loss set-off.' },
    ],
    eligibility: ['Anyone with taxable income or seeking a refund / credit / carry-forward.'],
    documents: ['PAN & Aadhaar', 'Form 16 / salary slips', 'Bank interest and capital gains statements', 'Investment proofs (80C, 80D etc.)', 'Business P&L and balance sheet (if applicable)'],
    process: [
      { step: 1, title: 'Data collection', desc: 'Simple checklist based on your income type.' },
      { step: 2, title: 'Computation', desc: 'Tax calculation and refund estimate.' },
      { step: 3, title: 'Review', desc: 'You verify draft return.' },
      { step: 4, title: 'Filing & e-verify', desc: 'Return filed and acknowledgement shared.' },
    ],
    faq: [
      { q: 'When is the ITR due?', a: 'Individuals: 31 July; audit cases: 31 October (subject to CBDT extensions).' },
      { q: 'Is filing Nil ITR useful?', a: 'Yes — it maintains financial history and helps with loans/visas.' },
      { q: 'What if I miss the deadline?', a: 'Belated returns can be filed with a late fee, but some benefits are lost.' },
    ],
    price: 'Starting ₹999', timeline: '2-5 Working Days',
    relatedSlugs: ['gst-return-filing', 'tds-return-filing', 'roc-annual-compliance'],
  }),
  'tds-return-filing': std({
    slug: 'tds-return-filing', title: 'TDS Return Filing',
    tagline: 'Quarterly TDS Returns — Filed and Certified',
    category: 'compliance', icon: ScrollText,
    image: 'https://images.unsplash.com/photo-1554224154-26032cdc0dcb?w=1200&q=80',
    imageAlt: 'TDS returns and Form 16 — TDS return filing',
    metaTitle: 'TDS Return Filing (24Q, 26Q, 27Q) — Utkarshudyam',
    metaDescription: 'Quarterly TDS return filing — 24Q, 26Q, 27Q. TAN, challan reconciliation, deductee mapping and Form 16/16A.',
    short: 'Quarterly TDS returns and Form 16/16A generation with challan reconciliation.',
    overview: 'Any person who deducts TDS must file quarterly TDS returns and issue Form 16/16A. Non-compliance attracts penalties and disallowance of expenses under Section 40(a).',
    whoNeeds: 'Employers, businesses paying rent, professional fees, commission, contractor payments and interest above threshold.',
    benefits: [
      { title: 'Avoid 40(a) Disallowance', desc: 'Expenses stay deductible.' },
      { title: 'Employee-Ready Form 16', desc: 'Timely issue for your team.' },
      { title: 'Penalty-Free', desc: 'No late fee under Section 234E.' },
      { title: 'Notice Management', desc: 'Traces notices resolved.' },
    ],
    eligibility: ['Anyone with a valid TAN and TDS obligation.'],
    documents: ['TAN', 'Deductee details', 'Challan payments (BSR, CIN, amount)', 'PAN of deductees'],
    process: [
      { step: 1, title: 'Data collection', desc: 'Payments and deductees compiled.' },
      { step: 2, title: 'Challan verification', desc: 'Payments matched with OLTAS.' },
      { step: 3, title: 'Return preparation', desc: '24Q / 26Q / 27Q drafted.' },
      { step: 4, title: 'Filing', desc: 'Uploaded via TRACES/portal.' },
      { step: 5, title: 'Form 16/16A', desc: 'Issued to deductees.' },
    ],
    faq: [
      { q: 'Do I need TAN for TDS?', a: 'Yes — TAN is mandatory to deduct and file TDS.' },
      { q: 'When are TDS returns due?', a: 'Quarterly — 31 Jul, 31 Oct, 31 Jan, 31 May.' },
      { q: 'Is Form 16 mandatory for salary?', a: 'Yes — must be issued by 15 June.' },
    ],
    price: 'Starting ₹999/quarter', timeline: 'Quarterly cycle',
    relatedSlugs: ['income-tax-return-filing', 'gst-return-filing'],
  }),
  'roc-annual-compliance': std({
    slug: 'roc-annual-compliance', title: 'ROC Annual Compliance',
    tagline: 'MCA Annual Filings for Companies & LLPs',
    category: 'compliance', icon: Landmark,
    image: 'https://images.unsplash.com/photo-1521791136064-7986c2920216?w=1200&q=80',
    imageAlt: 'Company annual compliance — ROC MCA filings',
    metaTitle: 'ROC Annual Compliance (AOC-4, MGT-7, Form 11) — Utkarshudyam',
    metaDescription: 'MCA annual filings for companies and LLPs — AOC-4, MGT-7, Form 11 and Form 8. Board meetings, statutory registers and Director KYC.',
    short: 'Annual MCA filings and secretarial compliance for Companies and LLPs.',
    overview: 'Every Company and LLP must file annual returns with the Registrar of Companies. Non-filing attracts hefty penalties and can lead to director disqualification and strike-off.',
    whoNeeds: 'All registered Companies and LLPs — active or dormant.',
    benefits: [
      { title: 'Avoid Penalties', desc: 'Late filings cost ₹100/day and can escalate.' },
      { title: 'Director Protection', desc: 'Avoid disqualification under 164(2).' },
      { title: 'Active Status', desc: 'Keep your company or LLP MCA-active.' },
    ],
    eligibility: ['All registered Companies and LLPs.'],
    documents: ['Books of accounts / Financial statements', 'Board resolutions', 'Shareholding & directors data', 'PAN / CIN / LLPIN'],
    process: [
      { step: 1, title: 'Books finalisation', desc: 'Accounts and audit finalised.' },
      { step: 2, title: 'Board / partner meetings', desc: 'Minutes and resolutions drafted.' },
      { step: 3, title: 'AOC-4 / Form 8', desc: 'Financials filed with MCA.' },
      { step: 4, title: 'MGT-7 / Form 11', desc: 'Annual return filed.' },
      { step: 5, title: 'Director KYC', desc: 'DIR-3 KYC done annually.' },
    ],
    faq: [
      { q: 'When are MCA annual filings due?', a: 'AOC-4 within 30 days of AGM; MGT-7 within 60 days.' },
      { q: 'What is Form 11 for LLP?', a: 'Annual return of LLP due by 30 May every year.' },
      { q: 'Are audits mandatory?', a: 'For companies — yes. For LLPs — only above prescribed thresholds.' },
    ],
    price: 'Starting ₹6,999/year', timeline: 'Annual cycle',
    relatedSlugs: ['private-limited-company-registration', 'llp-registration', 'income-tax-return-filing'],
  }),

  // ---------- IP / BRAND ----------
  'trademark-registration': std({
    slug: 'trademark-registration', title: 'Trademark Registration',
    tagline: 'Protect Your Brand Name and Logo Across India',
    category: 'digital', icon: Bookmark,
    image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=1200&q=80',
    imageAlt: 'Brand and logo protection — Trademark registration',
    metaTitle: 'Trademark Registration Online in India — Utkarshudyam',
    metaDescription: 'Register your trademark online. Free trademark search, class selection, application filing and objection response — end-to-end support.',
    short: 'Legally protect your brand name, logo or tagline against copying — valid across India for 10 years.',
    overview: 'A trademark is a unique brand identifier — name, logo, tagline or combination — registered under the Trade Marks Act, 1999. Registration gives 10-year renewable protection, TM/® rights and enforceable legal remedies.',
    whoNeeds: 'Any business owner who wants to protect their brand identity.',
    benefits: [
      { title: 'Legal Protection', desc: 'Exclusive rights to use the mark.' },
      { title: 'Brand Asset', desc: 'Trademarks are transferable and licensable.' },
      { title: '10-Year Validity', desc: 'Indefinitely renewable.' },
      { title: 'TM/® Symbol', desc: 'TM at filing, ® once registered.' },
    ],
    eligibility: ['Individuals and businesses (any structure).', 'A distinctive name / logo / tagline.'],
    documents: ['Applicant PAN & Aadhaar', 'Business proof (Udyam / GST) for MSME discount', 'Logo (if applicable)', 'List of goods/services', 'Signed authorization (TM-48)'],
    process: [
      { step: 1, title: 'Trademark search', desc: 'Free search across the Register.' },
      { step: 2, title: 'Class selection', desc: 'We identify the correct Nice class(es).' },
      { step: 3, title: 'Application filing', desc: 'TM-A filed with the Registry.' },
      { step: 4, title: 'Examination & journal', desc: 'Registry examination and publication.' },
      { step: 5, title: 'Objection / opposition handling', desc: 'If applicable.' },
      { step: 6, title: 'Registration certificate', desc: '® granted after clearance.' },
    ],
    faq: [
      { q: 'What is the government fee?', a: '₹4,500 (individual / MSME / startup) or ₹9,000 per class per application.' },
      { q: 'How long is registration valid?', a: '10 years — renewable indefinitely.' },
      { q: 'Do I need to register in each state?', a: 'No — trademark registration is pan-India.' },
    ],
    price: 'Starting ₹1,999 + Govt. Fee', timeline: '6-24 Months (Registry timeline)',
    relatedSlugs: ['copyright-registration', 'patent-registration', 'startup-india-certificate'],
  }),
  'copyright-registration': std({
    slug: 'copyright-registration', title: 'Copyright Registration',
    tagline: 'Protect Your Original Creative Work',
    category: 'digital', icon: Copyright,
    image: 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=1200&q=80',
    imageAlt: 'Creative work protection — Copyright registration',
    metaTitle: 'Copyright Registration Online in India — Utkarshudyam',
    metaDescription: 'Register copyright for software, artwork, books, music and other creative works. Lifetime protection with expert filing.',
    short: 'Protect your literary, artistic, musical or software work with copyright registration.',
    overview: 'The Copyright Act, 1957 protects original works from unauthorized reproduction and use. Registration is optional but strongly recommended as legal proof of authorship and ownership.',
    whoNeeds: 'Authors, software developers, artists, musicians, designers and content creators.',
    benefits: [
      { title: 'Proof of Ownership', desc: 'Government-issued certificate.' },
      { title: 'Long Protection', desc: 'Lifetime + 60 years.' },
      { title: 'Legal Remedies', desc: 'Enforceable rights against infringement.' },
      { title: 'Monetisable', desc: 'Can be licensed and assigned.' },
    ],
    eligibility: ['Any original creative work.'],
    documents: ['Applicant details', 'Work sample (soft/hard copy)', 'NOC from author (if applicant is different)', 'Class of work'],
    process: [
      { step: 1, title: 'Consultation', desc: 'We identify the correct class of work.' },
      { step: 2, title: 'Application filing', desc: 'Form XIV with Copyright Office.' },
      { step: 3, title: 'Diary number', desc: '30-day waiting for objections.' },
      { step: 4, title: 'Examination', desc: 'Registrar reviews the application.' },
      { step: 5, title: 'Registration certificate', desc: 'Issued upon clearance.' },
    ],
    faq: [
      { q: 'How long is copyright protected?', a: 'Lifetime of author + 60 years (for most works).' },
      { q: 'Can software be copyrighted?', a: 'Yes — software code is copyrightable.' },
    ],
    price: 'Starting ₹4,999', timeline: '2-6 Months',
    relatedSlugs: ['trademark-registration', 'patent-registration'],
  }),
  'patent-registration': std({
    slug: 'patent-registration', title: 'Patent Registration',
    tagline: 'Turn Your Invention into a Protected Asset',
    category: 'digital', icon: Fingerprint,
    image: 'https://images.unsplash.com/photo-1581092919535-13d1beece1e2?w=1200&q=80',
    imageAlt: 'Invention design — Patent registration',
    metaTitle: 'Patent Registration Online in India — Utkarshudyam',
    metaDescription: 'File Indian patents — provisional & complete specification, patent search and prosecution. Expert patent attorneys.',
    short: 'Get a 20-year monopoly on your invention with a granted patent.',
    overview: 'A patent grants an inventor exclusive rights for 20 years, preventing others from making, using or selling the invention without permission. India follows a first-to-file system.',
    whoNeeds: 'Inventors, startups and R&D-driven companies with novel and industrially applicable inventions.',
    benefits: [
      { title: '20-Year Monopoly', desc: 'Exclusive rights over your invention.' },
      { title: 'Startup Discounts', desc: '80% fee rebate for DPIIT-recognised startups.' },
      { title: 'Fund-raise Boost', desc: 'Investors value IP assets.' },
      { title: 'License / Sell', desc: 'Monetise via licensing or assignment.' },
    ],
    eligibility: ['Novel, non-obvious and industrially applicable invention.', 'Not covered by exclusions under Section 3 of the Patents Act.'],
    documents: ['Invention description and drawings', 'Applicant / inventor details', 'Form 1, 2, 3, 5', 'Priority documents (if any)'],
    process: [
      { step: 1, title: 'Patent search', desc: 'Prior-art search to test novelty.' },
      { step: 2, title: 'Drafting', desc: 'Provisional or complete specification drafted.' },
      { step: 3, title: 'Filing', desc: 'Application filed with the Patent Office.' },
      { step: 4, title: 'Publication & request for examination', desc: 'Within statutory timeframes.' },
      { step: 5, title: 'Prosecution', desc: 'Reply to FER and hearings.' },
      { step: 6, title: 'Grant of patent', desc: 'Patent certificate issued.' },
    ],
    faq: [
      { q: 'What is a provisional patent?', a: 'A placeholder filing that secures priority date; complete specification must be filed within 12 months.' },
      { q: 'Is Indian patent valid abroad?', a: 'No — file separately or via PCT for international protection.' },
      { q: 'Are startups eligible for discounts?', a: 'Yes — DPIIT-recognised startups get 80% fee rebate.' },
    ],
    price: 'Starting ₹14,999 + Govt. Fee', timeline: '2-4 Years',
    relatedSlugs: ['trademark-registration', 'copyright-registration', 'startup-india-certificate'],
  }),

  // ---------- LICENSING ----------
  'bis-certification': std({
    slug: 'bis-certification', title: 'BIS / ISI Certification',
    tagline: 'Product Conformity Mark from the Bureau of Indian Standards',
    category: 'licensing', icon: ShieldCheck,
    image: 'https://images.unsplash.com/photo-1567789884554-0b844b597180?w=1200&q=80',
    imageAlt: 'Product testing lab — BIS ISI certification',
    metaTitle: 'BIS / ISI Certification for Products — Utkarshudyam',
    metaDescription: 'BIS ISI certification for products — mandatory and voluntary schemes. Documentation, testing coordination and BIS liaison.',
    short: 'Mandatory or voluntary product certification from BIS for quality conformity.',
    overview: 'The Bureau of Indian Standards issues ISI marks for over 400 products under mandatory certification schemes and thousands more under voluntary. Certain products cannot be sold in India without BIS certification.',
    whoNeeds: 'Manufacturers and importers of electronics, cement, steel, paints, kitchen appliances, cables, and other regulated products.',
    benefits: [
      { title: 'Legal Compliance', desc: 'Mandatory for many products.' },
      { title: 'Consumer Confidence', desc: 'ISI mark signals quality.' },
      { title: 'Market Access', desc: 'Unlocks retail and tender opportunities.' },
    ],
    eligibility: ['Registered manufacturing unit.', 'Product falls under a BIS standard.'],
    documents: ['Business & factory details', 'Product design and test reports', 'Production process flow', 'Manufacturing licences', 'Trademark / brand approval'],
    process: [
      { step: 1, title: 'Standard identification', desc: 'Correct IS standard mapped.' },
      { step: 2, title: 'Application filing', desc: 'Form V / Form I filed with BIS.' },
      { step: 3, title: 'Sample testing', desc: 'At BIS-recognised labs.' },
      { step: 4, title: 'Factory inspection', desc: 'By BIS officers.' },
      { step: 5, title: 'Licence granted', desc: 'ISI licence issued.' },
    ],
    faq: [
      { q: 'Is BIS mandatory for all products?', a: 'Only for products under the mandatory certification list; check applicable IS standard.' },
      { q: 'How long is licence valid?', a: '1–2 years, renewable.' },
    ],
    price: 'Starting ₹19,999', timeline: '60-90 Working Days',
    relatedSlugs: ['iso-certification', 'cdsco-license', 'import-export-code'],
  }),
  'cdsco-license': std({
    slug: 'cdsco-license', title: 'CDSCO Drug & Medical Device License',
    tagline: 'Central Drugs Standard Control Organisation Licensing',
    category: 'licensing', icon: Pill,
    image: 'https://images.unsplash.com/photo-1587854692152-cbe660dbde88?w=1200&q=80',
    imageAlt: 'Pharma manufacturing — CDSCO license',
    metaTitle: 'CDSCO License for Drugs & Medical Devices — Utkarshudyam',
    metaDescription: 'CDSCO licence for drug import, manufacture and medical devices — Form 41/42, MD-14/15/40/41/42 support with expert liaison.',
    short: 'CDSCO approvals for drugs, cosmetics and medical device imports and manufacturing.',
    overview: 'CDSCO regulates drugs, cosmetics and medical devices in India. Import, manufacture and sale of these products need CDSCO licences and Form 10 / Form 41 approvals.',
    whoNeeds: 'Importers, manufacturers and wholesalers of drugs, cosmetics and medical devices.',
    benefits: [
      { title: 'Legal Import / Sale', desc: 'Only CDSCO-licensed entities can operate.' },
      { title: 'Product Registration', desc: 'Individual products registered.' },
      { title: 'Market Trust', desc: 'CDSCO-licensed products preferred by hospitals.' },
    ],
    eligibility: ['Registered business.', 'Qualified person on record (as applicable).', 'Approved premises.'],
    documents: ['Business registration', 'Premises layout', 'QP degree & experience', 'Product details / DMF', 'Test reports'],
    process: [
      { step: 1, title: 'Category mapping', desc: 'Correct licence type identified.' },
      { step: 2, title: 'Application on SUGAM', desc: 'CDSCO portal filing.' },
      { step: 3, title: 'Queries / inspection', desc: 'CDSCO clarifications.' },
      { step: 4, title: 'Licence granted', desc: 'Licence delivered.' },
    ],
    faq: [
      { q: 'Do medical devices need CDSCO?', a: 'Yes — most regulated categories under MDR 2017.' },
      { q: 'How long is licence valid?', a: 'Typically 3–5 years subject to category.' },
    ],
    price: 'Starting ₹29,999', timeline: '30-90 Working Days',
    relatedSlugs: ['bis-certification', 'import-export-code'],
  }),
  'pollution-noc': std({
    slug: 'pollution-noc', title: 'Pollution NOC / CTE-CTO',
    tagline: 'State Pollution Board Consent to Establish & Operate',
    category: 'licensing', icon: Recycle,
    image: 'https://images.unsplash.com/photo-1444492696363-332accfd928f?w=1200&q=80',
    imageAlt: 'Sustainable operations — Pollution NOC',
    metaTitle: 'Pollution NOC / CTE-CTO from State Pollution Board — Utkarshudyam',
    metaDescription: 'Obtain Consent to Establish (CTE) and Consent to Operate (CTO) from your State Pollution Control Board — expert documentation and liaison.',
    short: 'Environmental clearance from your State Pollution Control Board.',
    overview: 'Certain manufacturing and service units need Consent to Establish (CTE) before starting operations and Consent to Operate (CTO) after commissioning, from the State Pollution Control Board.',
    whoNeeds: 'Manufacturing units, hospitals, hotels, packaging units and other listed activities.',
    benefits: [
      { title: 'Legal Compliance', desc: 'Mandatory for regulated activities.' },
      { title: 'Sustainable Operations', desc: 'Structured environmental control.' },
      { title: 'Tender Eligibility', desc: 'Required for many contracts.' },
    ],
    eligibility: ['Registered unit in a scheduled activity.'],
    documents: ['Site plan and process flow', 'Effluent / emission details', 'Business registration', 'Land / lease documents'],
    process: [
      { step: 1, title: 'Category & fee calc', desc: 'Red / Orange / Green mapping.' },
      { step: 2, title: 'Application filing', desc: 'Board portal submission.' },
      { step: 3, title: 'Inspection', desc: 'Board officers visit.' },
      { step: 4, title: 'Consent order', desc: 'CTE / CTO issued.' },
    ],
    faq: [
      { q: 'What is the validity?', a: '1–15 years depending on Red/Orange/Green category.' },
      { q: 'Is CTE needed before CTO?', a: 'Yes — CTE first, CTO after commissioning.' },
    ],
    price: 'Starting ₹9,999', timeline: '30-60 Working Days',
    relatedSlugs: ['bis-certification', 'iso-certification', 'fssai-registration'],
  }),

  // ---------- TRADE ----------
  'import-export-code': std({
    slug: 'import-export-code', title: 'Import Export Code (IEC)',
    tagline: 'Mandatory 10-Digit Code for International Trade',
    category: 'licensing', icon: Ship,
    image: 'https://images.unsplash.com/photo-1494412651409-8963ce7935a7?w=1200&q=80',
    imageAlt: 'Shipping containers and cargo — Import Export Code',
    metaTitle: 'Import Export Code (IEC) Registration Online — Utkarshudyam',
    metaDescription: 'Get IEC online for international trade. Mandatory for imports and exports. Lifetime validity, quick DGFT filing.',
    short: 'The 10-digit code from DGFT that allows a business to legally import or export from India.',
    overview: 'The Import Export Code (IEC) is issued by the Directorate General of Foreign Trade (DGFT). Without an IEC, a business cannot clear customs or receive foreign remittances for goods.',
    whoNeeds: 'Any business that imports or exports goods; sometimes required for services with foreign exchange.',
    benefits: [
      { title: 'Legal Trade', desc: 'Mandatory for customs clearance.' },
      { title: 'Lifetime Validity', desc: 'One-time registration.' },
      { title: 'DGFT Benefits', desc: 'Access RoDTEP, MEIS/RoSCTL etc.' },
      { title: 'Forex Access', desc: 'Facilitates foreign remittances.' },
    ],
    eligibility: ['Any registered Indian business / individual with PAN.'],
    documents: ['PAN', 'Aadhaar / passport', 'Address proof', 'Bank certificate / cancelled cheque', 'Business registration proof'],
    process: [
      { step: 1, title: 'Application prep', desc: 'DGFT portal application prepared.' },
      { step: 2, title: 'e-signature / DSC', desc: 'Signature applied.' },
      { step: 3, title: 'Filing', desc: 'Portal submission.' },
      { step: 4, title: 'IEC issued', desc: '10-digit code issued digitally.' },
    ],
    faq: [
      { q: 'Is IEC needed for service exporters?', a: 'Only if they claim benefits under Foreign Trade Policy; otherwise not.' },
      { q: 'Do I need to renew IEC?', a: 'Yes — annual online update required, even if no changes.' },
    ],
    price: 'Starting ₹1,499', timeline: '2-5 Working Days',
    relatedSlugs: ['apeda-registration', 'gst-registration', 'bis-certification'],
  }),
  'apeda-registration': std({
    slug: 'apeda-registration', title: 'APEDA Registration',
    tagline: 'Agricultural and Processed Food Products Export Registration',
    category: 'licensing', icon: Leaf,
    image: 'https://images.unsplash.com/photo-1500595046743-cd271d694d30?w=1200&q=80',
    imageAlt: 'Agriculture export — APEDA registration',
    metaTitle: 'APEDA Registration for Agri Exporters — Utkarshudyam',
    metaDescription: 'APEDA Registration-Cum-Membership Certificate (RCMC) for scheduled agri and processed food exports.',
    short: 'RCMC from APEDA required for exporters of scheduled agri and processed food products.',
    overview: 'APEDA (Agricultural and Processed Food Products Export Development Authority) issues an RCMC that is mandatory for exporting scheduled products — fruits, vegetables, meat, dairy, cereals, and processed foods.',
    whoNeeds: 'Exporters of agri and processed food products listed under APEDA schedule.',
    benefits: [
      { title: 'Mandatory RCMC', desc: 'Required for scheduled product exports.' },
      { title: 'Export Subsidies', desc: 'Access APEDA financial assistance.' },
      { title: 'Trade Fair Support', desc: 'Global buyer connect programmes.' },
    ],
    eligibility: ['Valid IEC.', 'Exports of APEDA-scheduled products.'],
    documents: ['IEC certificate', 'PAN', 'Bank certificate', 'Business registration', 'Export details'],
    process: [
      { step: 1, title: 'APEDA portal registration', desc: 'Business profile creation.' },
      { step: 2, title: 'Documentation', desc: 'Uploads and product mapping.' },
      { step: 3, title: 'Application filing', desc: 'Payment and submission.' },
      { step: 4, title: 'RCMC issued', desc: 'Digital certificate.' },
    ],
    faq: [
      { q: 'How long is APEDA registration valid?', a: '5 years, renewable.' },
      { q: 'Is APEDA needed for all agri exports?', a: 'Only if the product is scheduled under APEDA.' },
    ],
    price: 'Starting ₹4,999', timeline: '7-15 Working Days',
    relatedSlugs: ['import-export-code', 'fssai-registration', 'iso-certification'],
  }),

  // ---------- FUNDING ----------
  'business-loan': std({
    slug: 'business-loan', title: 'Business Loan',
    tagline: 'Unsecured & Working Capital Loans for Growing Businesses',
    category: 'funding', icon: Banknote,
    image: 'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=1200&q=80',
    imageAlt: 'Business growth funding — Business loan',
    metaTitle: 'Business Loan Assistance — Utkarshudyam',
    metaDescription: 'Get unsecured business loans, CC / OD limits and working capital lines with expert advisory and DPR preparation.',
    short: 'Get unsecured business loans, CC/OD and working capital lines through expert advisory.',
    overview: 'We match your profile with the right lender — private banks, NBFCs and government-backed schemes — and prepare the documents and project report so that approval odds are high.',
    whoNeeds: 'Growing businesses that need capital for expansion, working capital or equipment.',
    benefits: [
      { title: 'Higher Approval Odds', desc: 'Right lender + strong documentation.' },
      { title: 'No Collateral Options', desc: 'MUDRA and CGTMSE-backed loans.' },
      { title: 'DPR Support', desc: 'Bank-ready project report prepared.' },
      { title: 'Faster Turnaround', desc: 'Documentation done right the first time.' },
    ],
    eligibility: ['Vintage of at least 12 months (varies by lender).', 'Positive credit history.', 'Bank statements and financials.'],
    documents: ['KYC of promoters', 'Business registration', 'Bank statements (12 months)', 'ITR (2–3 years)', 'GST returns (if applicable)', 'Financials / DPR'],
    process: [
      { step: 1, title: 'Profile assessment', desc: 'We evaluate eligibility & fit.' },
      { step: 2, title: 'Lender shortlist', desc: 'Right banks/NBFCs mapped.' },
      { step: 3, title: 'Documentation & DPR', desc: 'Bank-ready file assembled.' },
      { step: 4, title: 'Submission & follow-up', desc: 'Application and queries handled.' },
      { step: 5, title: 'Sanction & disbursal', desc: 'Loan disbursed to your account.' },
    ],
    faq: [
      { q: 'Do I need collateral?', a: 'Not always — MUDRA and CGTMSE schemes offer collateral-free loans.' },
      { q: 'How much can I borrow?', a: 'From ₹1 lakh to ₹5+ crore depending on profile.' },
      { q: 'What is the interest rate?', a: 'Ranges by profile and lender; we advise based on your case.' },
    ],
    price: 'Success-fee based', timeline: '10-30 Working Days',
    relatedSlugs: ['msme-mudra-loan', 'cgtmse-loan', 'project-report-preparation'],
  }),
  'msme-mudra-loan': std({
    slug: 'msme-mudra-loan', title: 'MSME / MUDRA Loan',
    tagline: 'Government-Backed Loans for Micro & Small Enterprises',
    category: 'funding', icon: IndianRupee,
    image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=1200&q=80',
    imageAlt: 'Small business owner — MUDRA loan',
    metaTitle: 'MSME MUDRA Loan (Shishu, Kishore, Tarun) — Utkarshudyam',
    metaDescription: 'MUDRA loans under PMMY — Shishu, Kishore, Tarun. Up to ₹10 lakh for micro enterprises with expert loan advisory.',
    short: 'PMMY loans up to ₹10 lakh — collateral-free, government-backed.',
    overview: 'The Pradhan Mantri Mudra Yojana (PMMY) provides collateral-free loans up to ₹10 lakh in three categories — Shishu (up to ₹50k), Kishore (₹50k–₹5L) and Tarun (₹5L–₹10L).',
    whoNeeds: 'Micro entrepreneurs, small manufacturers, service providers and traders.',
    benefits: [
      { title: 'No Collateral', desc: 'Collateral-free under PMMY guarantee.' },
      { title: 'Lower Interest', desc: 'Competitive rates.' },
      { title: 'Simple Process', desc: 'Streamlined KYC.' },
    ],
    eligibility: ['Non-corporate, non-farm SME.', 'Income-generating activity.', 'Valid PAN and business proof.'],
    documents: ['KYC of promoter', 'Udyam certificate', 'Bank statements (6 months)', 'Business plan / quotation'],
    process: [
      { step: 1, title: 'Category & lender fit', desc: 'Shishu / Kishore / Tarun mapping.' },
      { step: 2, title: 'Application preparation', desc: 'Documents finalised.' },
      { step: 3, title: 'Bank submission', desc: 'Application at partner bank.' },
      { step: 4, title: 'Disbursal', desc: 'Loan credited on approval.' },
    ],
    faq: [
      { q: 'Do I need Udyam for MUDRA?', a: 'Recommended — improves approval odds significantly.' },
      { q: 'What is the max limit?', a: '₹10 lakh under Tarun scheme.' },
    ],
    price: 'Success-fee based', timeline: '10-30 Working Days',
    relatedSlugs: ['business-loan', 'cgtmse-loan', 'msme-udyam-registration'],
  }),
  'cgtmse-loan': std({
    slug: 'cgtmse-loan', title: 'CGTMSE Loan',
    tagline: 'Credit Guarantee Backed Collateral-Free Loans',
    category: 'funding', icon: Wallet,
    image: 'https://images.unsplash.com/photo-1553729459-efe14ef6055d?w=1200&q=80',
    imageAlt: 'Business finance — CGTMSE loan',
    metaTitle: 'CGTMSE Loan — Collateral-Free MSME Credit — Utkarshudyam',
    metaDescription: 'CGTMSE-backed loans up to ₹5 crore for MSMEs — expert advisory, documentation and lender liaison.',
    short: 'Credit Guarantee Fund Trust-backed loans up to ₹5 crore for MSMEs, without collateral.',
    overview: 'CGTMSE is a government-run credit guarantee scheme that lets lenders extend collateral-free loans to MSMEs. The guarantee covers a large portion of the credit facility.',
    whoNeeds: 'Manufacturing and service MSMEs.',
    benefits: [
      { title: 'Collateral-Free', desc: 'Up to ₹5 crore under CGTMSE cover.' },
      { title: 'Wider Lender Choice', desc: 'Public and private banks partner.' },
      { title: 'Priority Sector', desc: 'Fast-tracked processing.' },
    ],
    eligibility: ['MSME with Udyam registration.', 'Positive credit history.'],
    documents: ['Udyam certificate', 'KYC of promoters', 'Financials', 'Bank statements', 'Project report'],
    process: [
      { step: 1, title: 'Eligibility assessment', desc: 'CGTMSE fit analysed.' },
      { step: 2, title: 'DPR preparation', desc: 'Bank-ready project report.' },
      { step: 3, title: 'Bank submission', desc: 'Application with partner banks.' },
      { step: 4, title: 'CGTMSE coverage', desc: 'Credit guarantee applied.' },
      { step: 5, title: 'Disbursal', desc: 'Loan disbursed to your account.' },
    ],
    faq: [
      { q: 'Is Udyam mandatory?', a: 'Yes — MSME registration is required.' },
      { q: 'What is the maximum loan?', a: 'Up to ₹5 crore per borrower under current guidelines.' },
    ],
    price: 'Success-fee based', timeline: '15-45 Working Days',
    relatedSlugs: ['business-loan', 'msme-mudra-loan', 'project-report-preparation'],
  }),
  'project-report-preparation': std({
    slug: 'project-report-preparation', title: 'Project Report (DPR) Preparation',
    tagline: 'Bank-Ready Detailed Project Reports for Loans',
    category: 'funding', icon: PieChart,
    image: 'https://images.unsplash.com/photo-1454165205744-3b78555e5572?w=1200&q=80',
    imageAlt: 'Financial project analysis — DPR preparation',
    metaTitle: 'Project Report / DPR Preparation for Loans — Utkarshudyam',
    metaDescription: 'Detailed Project Reports (DPR) for bank loans, subsidies and schemes. Financial projections, ratios and viability analysis.',
    short: 'Bank-ready DPRs with financial projections, ratios and viability analysis.',
    overview: 'A Detailed Project Report presents your business plan, financials and viability in a bank-acceptable format. It is required for MUDRA, CGTMSE, term loans and government subsidy applications.',
    whoNeeds: 'Any business applying for loans or government subsidy schemes.',
    benefits: [
      { title: 'Bank Ready', desc: 'Structured to lenders\' expectations.' },
      { title: 'Higher Approval Odds', desc: 'Strong narrative + numbers.' },
      { title: 'Scheme Applications', desc: 'Fits PMEGP, CGTMSE, CLCSS etc.' },
    ],
    eligibility: ['Any registered / proposed business.'],
    documents: ['Business idea / plan', 'Cost estimates / quotations', 'Promoter details', 'Sales projections'],
    process: [
      { step: 1, title: 'Kick-off call', desc: 'Understand business and objective.' },
      { step: 2, title: 'Data collection', desc: 'Costs, revenue and margins.' },
      { step: 3, title: 'Modelling', desc: '5-year projections built.' },
      { step: 4, title: 'DPR drafting', desc: 'Report drafted per bank format.' },
      { step: 5, title: 'Delivery', desc: 'Editable and PDF versions shared.' },
    ],
    faq: [
      { q: 'Do banks always require DPR?', a: 'For most term loans above ₹10 lakh — yes.' },
      { q: 'How long does it take?', a: '5–10 working days once inputs are received.' },
    ],
    price: 'Starting ₹4,999', timeline: '5-10 Working Days',
    relatedSlugs: ['business-loan', 'cgtmse-loan', 'msme-mudra-loan'],
  }),

  // ---------- DIGITAL / WEB ----------
  'website-development': std({
    slug: 'website-development', title: 'Website Development',
    tagline: 'High-Converting Business Websites',
    category: 'digital', icon: Monitor,
    image: 'https://images.unsplash.com/photo-1467232004584-a241de8bcf5d?w=1200&q=80',
    imageAlt: 'Modern web development — Website design',
    metaTitle: 'Website Development for Businesses — Utkarshudyam',
    metaDescription: 'Professional website development — corporate sites, e-commerce, landing pages and CMS. SEO-friendly, mobile-first and lead-optimised.',
    short: 'Modern, mobile-first websites that convert visitors into enquiries.',
    overview: 'From a simple corporate site to a full e-commerce or CRM-integrated web app, we design and build websites that look professional and drive business results.',
    whoNeeds: 'Any business that wants a professional online presence and lead generation.',
    benefits: [
      { title: 'Modern Design', desc: 'Premium, mobile-first look.' },
      { title: 'SEO Ready', desc: 'Optimised for Google visibility.' },
      { title: 'Lead Focused', desc: 'Forms, CTAs and analytics wired.' },
      { title: 'CMS / Editable', desc: 'Update content without a developer.' },
    ],
    eligibility: ['Any business.'],
    documents: ['Logo and brand guidelines', 'Content (or brief for us to write)', 'Domain / hosting details'],
    process: [
      { step: 1, title: 'Discovery call', desc: 'Understand goals and audience.' },
      { step: 2, title: 'Design mock-ups', desc: 'Wireframes and visual design.' },
      { step: 3, title: 'Development', desc: 'Site built and content added.' },
      { step: 4, title: 'Testing', desc: 'Cross-device QA.' },
      { step: 5, title: 'Launch', desc: 'Deployed to production.' },
      { step: 6, title: 'Support', desc: 'Post-launch maintenance.' },
    ],
    faq: [
      { q: 'How long does a website take?', a: '2–6 weeks depending on scope.' },
      { q: 'Do you also do e-commerce?', a: 'Yes — with Shopify, WooCommerce or custom stacks.' },
    ],
    price: 'Starting ₹14,999', timeline: '15-45 Working Days',
    relatedSlugs: ['digital-marketing', 'trademark-registration'],
  }),
  'digital-marketing': std({
    slug: 'digital-marketing', title: 'Digital Marketing',
    tagline: 'SEO, Google Ads, Social Media & Content — Done Right',
    category: 'digital', icon: Megaphone,
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1200&q=80',
    imageAlt: 'Analytics dashboard — Digital marketing services',
    metaTitle: 'Digital Marketing Services for Businesses — Utkarshudyam',
    metaDescription: 'SEO, Google Ads, Meta Ads, social media, and content marketing. Data-driven strategies to grow enquiries and revenue.',
    short: 'SEO + performance ads + social + content — measured against enquiries and revenue.',
    overview: 'We build growth engines — a mix of SEO, paid ads, content and email that compound over time. Every campaign is measured and reported.',
    whoNeeds: 'Businesses that want predictable lead flow and better brand visibility.',
    benefits: [
      { title: 'Predictable Leads', desc: 'Structured funnel management.' },
      { title: 'Data Driven', desc: 'Weekly reports and clear KPIs.' },
      { title: 'Full Stack', desc: 'SEO + Ads + Social + Content.' },
      { title: 'CRM Ready', desc: 'Leads pushed to your CRM.' },
    ],
    eligibility: ['Any business with a website.'],
    documents: ['Business overview', 'Existing analytics access', 'Brand assets'],
    process: [
      { step: 1, title: 'Audit', desc: 'Website, ads and SEO audit.' },
      { step: 2, title: 'Strategy', desc: 'Roadmap and KPIs.' },
      { step: 3, title: 'Execution', desc: 'Campaigns and content live.' },
      { step: 4, title: 'Optimisation', desc: 'Weekly experiments.' },
      { step: 5, title: 'Reporting', desc: 'Monthly business review.' },
    ],
    faq: [
      { q: 'How soon will I see results?', a: 'Ads: within days. SEO: 3–6 months for compounding traffic.' },
      { q: 'Is content included?', a: 'Yes — blogs, ad creatives and social posts as per plan.' },
    ],
    price: 'Starting ₹19,999/month', timeline: 'Ongoing engagement',
    relatedSlugs: ['website-development', 'trademark-registration'],
  }),
};

// Convenience list
export const allServiceSlugs = Object.keys(services);
export const getService = (slug) => services[slug];
export const getRelated = (slug) => (services[slug]?.relatedSlugs || []).map(getService).filter(Boolean);
